/**
 * The occurrence sheet, the series editor and the subscribe card a forecast hosts for a series it does not
 * own the account of (TMMAPP-0101: the envelope's Cashflow forecast; the same wiring the virtual account's
 * forecast tab carries). One modal at a time (DLG-005): the sheet closes before the editor opens. Acting
 * on a series needs the series' own account (`accountId` from the series), so the account is looked up per
 * series rather than taken from the page. The sheet and the editor read the owned envelopes: a page that
 * provides them (Recurring, Cash flow) is used; elsewhere (an envelope's or a virtual account's forecast)
 * the host brings its own provider while one is open, and while the editor is being opened, so going from
 * the sheet to the editor keeps it mounted and reads the lists once (TMMAPP-0147, TMMAPP-0149).
 */
import { useContext, useState, type ReactNode } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { accountsApi, type Account } from '@/api/accounts';
import { ErrorCodes, isApiError } from '@/api/errors';
import { plannedApi, type Occurrence, type Series } from '@/api/planned';
import { canEditAllocations, isOwnerAccount } from '@/features/accounts/accountAccess';
import { OwnedEnvelopesProvider } from '@/features/accounts/OwnedEnvelopesProvider';
import { OwnedEnvelopesContext } from '@/features/accounts/useOwnedEnvelopes';
import { OccurrenceSheet } from '@/features/planned/OccurrenceSheet';
import { plannedErrorKey } from '@/features/planned/plannedErrors';
import { recurringSeriesLink } from '@/features/planned/plannedRoutes';
import { SeriesEditorDialog, type EditorMode } from '@/features/planned/SeriesEditorDialog';
import { SubscribeCard } from '@/features/subscribe/SubscribeCard';

interface SeriesHostOptions {
  /** The amount currency of the sheet when the series' own account has none. */
  currency: string | null;
  onChanged: () => void | Promise<void>;
  onNotify: (message: string) => void;
}

interface SeriesHost {
  openOccurrence: (seriesId: number, originalOn: string) => Promise<void>;
  openEditor: (mode: EditorMode, seriesId: number, fromDate?: string) => Promise<void>;
  /** Render once, anywhere in the tab. */
  node: ReactNode;
}

export function useSeriesHost({ currency, onChanged, onNotify }: SeriesHostOptions): SeriesHost {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ownedEnvelopes = useContext(OwnedEnvelopesContext);
  const [sheet, setSheet] = useState<{
    occ: Occurrence;
    series: Series;
    account: Account;
  } | null>(null);
  const [editor, setEditor] = useState<{
    mode: EditorMode;
    series: Series;
    fromDate?: string;
  } | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [subscribe, setSubscribe] = useState(false);
  const [opening, setOpening] = useState(false);

  function fail(err: unknown) {
    if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) setSubscribe(true);
    else onNotify(t(plannedErrorKey(err) ?? 'planned.errorGeneric'));
  }

  async function openOccurrence(seriesId: number, originalOn: string) {
    try {
      const [series, list, rows] = await Promise.all([
        plannedApi.getSeries(seriesId),
        plannedApi.timeline(seriesId, { from: originalOn, to: originalOn }),
        accountsApi.list(),
      ]);
      const occ = list.find((row) => row.originalOn === originalOn) ?? list[0];
      if (occ === undefined) return;
      const account =
        rows.find((row) => row.id === series.accountId) ??
        (await accountsApi.get(series.accountId));
      setAccounts(rows);
      setSheet({ occ, series, account });
    } catch (err) {
      fail(err);
    }
  }

  async function openEditor(mode: EditorMode, seriesId: number, fromDate?: string) {
    setOpening(true);
    try {
      const [series, rows] = await Promise.all([
        plannedApi.getSeries(seriesId),
        accounts.length > 0 ? Promise.resolve(accounts) : accountsApi.list(),
      ]);
      setAccounts(rows);
      setEditor({ mode, series, ...(fromDate !== undefined ? { fromDate } : {}) });
    } catch (err) {
      fail(err);
    } finally {
      setOpening(false);
    }
  }

  const dialogs = (
    <>
      {sheet !== null && (
        <OccurrenceSheet
          occurrence={sheet.occ}
          series={sheet.series}
          account={sheet.account}
          owner={isOwnerAccount(sheet.account)}
          currency={sheet.account.currency ?? sheet.account.operatingCurrency ?? currency}
          onClose={() => setSheet(null)}
          onChanged={async () => {
            await onChanged();
            const tl = await plannedApi.timeline(sheet.occ.seriesId, {
              from: sheet.occ.originalOn,
              to: sheet.occ.originalOn,
            });
            const next = tl.find((o) => o.originalOn === sheet.occ.originalOn);
            if (next) setSheet({ ...sheet, occ: next });
          }}
          onEditFollowing={() => {
            setSheet(null);
            void openEditor('following', sheet.series.id, sheet.occ.originalOn);
          }}
          onEditSeries={() => {
            setSheet(null);
            void openEditor('series', sheet.series.id);
          }}
          onOpenSeries={() => {
            const link = recurringSeriesLink(sheet.series.id);
            void navigate(link.to, link.options);
          }}
          onNotify={onNotify}
        />
      )}
      {editor !== null && (
        <SeriesEditorDialog
          mode={editor.mode}
          accounts={accounts.filter((a) => isOwnerAccount(a) || canEditAllocations(a))}
          series={editor.series}
          fromDate={editor.fromDate}
          onClose={() => setEditor(null)}
          onSaved={(message) => {
            onNotify(message);
            setEditor(null);
            void onChanged();
          }}
        />
      )}
    </>
  );

  const node = (
    <>
      {(sheet !== null || editor !== null || opening) &&
        (ownedEnvelopes !== null ? (
          dialogs
        ) : (
          <OwnedEnvelopesProvider>{dialogs}</OwnedEnvelopesProvider>
        ))}
      {subscribe && (
        <Dialog
          open
          onClose={() => setSubscribe(false)}
          maxWidth="xs"
          aria-label={t('subscribe.cardTitle')}
        >
          <DialogContent>
            <SubscribeCard />
          </DialogContent>
        </Dialog>
      )}
    </>
  );

  return { openOccurrence, openEditor, node };
}
